import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CheckIcon from "@material-ui/icons/Check";
import momentTz from "moment-timezone";
import { CheckoutIcon } from "@sonder/ui/source/icons";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { GetInquiryCheckoutsConnectionsInquiry } from "core/queries/GetInquiryCheckouts";
import { useSetGuestActualDepartureTime } from "core/queries/SetGuestActualDepartureTime";
import { formatAccountName } from "tasks/helpers";

const HeroImage = styled.div`
  align-items: center;
  background: ${colors.blue3};
  border-radius: 0.3rem 0.3rem 0 0;
  color: ${colors.white};
  display: flex;
  justify-content: center;
  margin: -1.5rem -1.5rem 1rem;
  padding: 1.5rem;
`;

const ModalContent = styled.div`
  p {
    margin: 0 0 0.5rem;
  }
`;

const StyledList = styled.ul`
  list-style: none;
  padding-left: 0.5rem;

  li {
    align-items: center;
    display: flex;
    margin-top: 0.5rem;
  }
`;

const StyledCheckmark = styled(CheckIcon)`
  margin-right: 0.5rem;
`;

interface CheckoutModalType extends Pick<ModalProps, "isOpen" | "onClose"> {
  booking: GetInquiryCheckoutsConnectionsInquiry | null;
}

export const CheckoutModal = ({
  booking,
  isOpen,
  onClose,
}: CheckoutModalType) => {
  const { track } = useSegment();
  const errorReporter = useErrorReporterFiltered();
  const [cachedBooking, setCachedBooking] = useState(booking);
  const timezone = useCurrentUserTimezone();
  const [onDepart, { loading: isDeparting }] = useSetGuestActualDepartureTime({
    awaitRefetchQueries: true,
    refetchQueries: ["GetInquiryCheckouts"],
  });

  useEffect(() => {
    if (isOpen) {
      setCachedBooking(booking);
    }
  }, [isOpen]);
  const isSubmitDisabled = !cachedBooking || isDeparting;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => {
        const onHandleCancel = async () => {
          track({
            eventName: "checkout_modal",
            action: "click",
            element: "cancel",
            props: {
              bookingId: cachedBooking?.id,
            },
          });
          closeModal();
        };
        const onHandleSubmit = async () => {
          const actualDepartureTime = momentTz.tz(timezone);
          track({
            eventName: "checkout_modal",
            action: "click",
            element: "submit",
            props: {
              bookingId: cachedBooking?.id,
            },
          });
          try {
            await onDepart({
              variables: {
                actualDepartureTime,
                inquiryId: cachedBooking?.id || "",
              },
            });
            closeModal();
          } catch (error) {
            errorReporter.notify(error);
          }
        };
        return (
          <>
            <HeroImage>
              <CheckoutIcon />
            </HeroImage>
            <ModalHeader title="Checking out?" onClose={closeModal} />
            <ModalContent>
              <p>
                <b>Guest name: </b>
                {formatAccountName(cachedBooking?.account)}
              </p>
              <p>
                <b>Room #: </b>
                {cachedBooking?.unit.aptNumber}
              </p>
              <StyledList>
                <li>
                  <StyledCheckmark /> Confirm that the guest has all their
                  belongings.
                </li>
                <li>
                  <StyledCheckmark /> The guest will not be able to access the
                  Sonder once checked out.
                </li>
                <li>
                  <StyledCheckmark /> Once checked out, this action cannot be
                  undone.
                </li>
              </StyledList>
            </ModalContent>
            <ModalFooter>
              <InputGroup>
                <Button onClick={onHandleCancel}>No, cancel</Button>
                <PrimaryButton
                  disabled={isSubmitDisabled}
                  onClick={onHandleSubmit}
                >
                  Yes, checkout
                </PrimaryButton>
              </InputGroup>
            </ModalFooter>
          </>
        );
      }}
    </Modal>
  );
};
